import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ErroDaApi } from '../../shared/api/erros';
import { formatarDataHora } from '../../shared/dominio/datas';
import { textoDeLeituraDesistida, textoDeLeituraSemAgente } from '../../shared/falhasDeEmail/avisoDeLeitura';
import { opcoesDoResumoDasFalhasDeEmail } from '../../shared/falhasDeEmail/resumo';
import { ConfirmacaoHumana } from '../../shared/ui/ConfirmacaoHumana';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { Identificador } from '../../shared/ui/Identificador';

import {
  CODIGO_CAIXA_SEM_ROTA,
  CODIGO_COLETA_DESLIGADA,
  CODIGO_REPROCESSO_EM_ANDAMENTO,
  CODIGO_TRIAGEM_EM_ANDAMENTO,
  LOTE_DA_TRIAGEM,
  LOTE_DO_REPROCESSO,
  TAMANHO_DE_PAGINA,
  chavesDasFalhasDeEmail,
  opcoesDasFalhasDeEmail,
  reprocessarFalhasDeEmail,
  triarEmailsDaCaixa,
} from './api/falhasDeEmail';
import {
  ROTULOS_DE_ETAPA,
  type FalhaDeEmail,
  type ResultadoDaTriagem,
  type ResultadoDoReprocesso,
} from './api/tipos';

/** As duas ações da tela, cada uma com o próprio botão, diálogo e resultado. */
type Acao = 'coleta' | 'triagem';

/** O que a pessoa vê depois de uma ação: o resultado da última, e só dela. */
type Desfecho =
  | { readonly acao: 'coleta'; readonly resultado: ResultadoDoReprocesso }
  | { readonly acao: 'triagem'; readonly resultado: ResultadoDaTriagem };

/** Muito além de qualquer fila real; evita mandar `page=1e20` para a API. */
const PAGINA_MAXIMA = 100_000;

function lerPagina(bruto: string | null): number {
  const numero = Number(bruto);
  return Number.isSafeInteger(numero) && numero >= 1 && numero <= PAGINA_MAXIMA ? numero : 1;
}

function contagem(n: number, umItem: string, variosItens: string): string {
  return `${String(n)} ${n === 1 ? umItem : variosItens}`;
}

/** O que a pessoa lê depois do reprocesso. Nada vira sucesso silencioso. */
function resumoDoReprocesso(resultado: ResultadoDoReprocesso): string {
  if (resultado.tentadas === 0) {
    return 'Nenhuma falha de coleta estava pronta para ser reprocessada.';
  }
  const fila = resultado.aindaHaFila ? ' Ainda há fila: reprocesse de novo para tentar as próximas.' : '';
  return (
    `Tentamos ${contagem(resultado.tentadas, 'falha', 'falhas')} de coleta da caixa configurada: ` +
    `${contagem(resultado.reprocessadas, 'reprocessada', 'reprocessadas')}, ` +
    `${contagem(resultado.naoEncontradas, 'não encontrada', 'não encontradas')} no provedor e ` +
    `${String(resultado.falharam)} com erro.${fila} ` +
    'As falhas de triagem têm o botão próprio; as que esgotaram as tentativas e as de outra caixa não são reprocessadas aqui.'
  );
}

/** O que a pessoa lê depois da triagem. A triagem age na caixa: as contagens são de todos os e-mails vistos. */
function resumoDaTriagem(resultado: ResultadoDaTriagem): string {
  if (resultado.vistos === 0) {
    return 'Nenhum e-mail da caixa estava sem ticket.';
  }
  const fila = resultado.aindaHaFila ? ' Ainda há e-mails sem ticket: use Triar e-mails da caixa de novo para atribuir os próximos.' : '';
  return (
    `${contagem(resultado.vistos, 'e-mail visto', 'e-mails vistos')}: ` +
    `${contagem(resultado.vinculados, 'ligado a ticket existente', 'ligados a tickets existentes')}, ` +
    `${contagem(resultado.abertos, 'ticket aberto', 'tickets abertos')}, ` +
    `${contagem(resultado.reabertos, 'reaberto', 'reabertos')}, ` +
    `${contagem(resultado.falharam, 'falhou', 'falharam')}.${fila}`
  );
}

/** "Nada aconteceu" não é sucesso: verde só quando algo foi feito sem ressalva. */
function severidadeDoDesfecho(desfecho: Desfecho): 'success' | 'warning' | 'info' {
  if (desfecho.acao === 'triagem') {
    if (desfecho.resultado.vistos === 0) return 'info';
    return desfecho.resultado.falharam > 0 ? 'warning' : 'success';
  }
  if (desfecho.resultado.tentadas === 0) return 'info';
  return desfecho.resultado.falharam > 0 || desfecho.resultado.naoEncontradas > 0 ? 'warning' : 'success';
}

function textoDoDesfecho(desfecho: Desfecho): string {
  return desfecho.acao === 'triagem' ? resumoDaTriagem(desfecho.resultado) : resumoDoReprocesso(desfecho.resultado);
}

/** A mensagem de cada desfecho de erro do `POST`, pelo `codigo` quando existe. `null` = a sessão expirou. */
function mensagemDeErro(acao: Acao, causa: unknown): string | null {
  if (causa instanceof ErroDaApi) {
    if (causa.status === 401) return null;
    if (acao === 'coleta') {
      if (causa.codigo === CODIGO_REPROCESSO_EM_ANDAMENTO) {
        return 'Outra rodada de reprocesso já está em andamento. Aguarde alguns instantes e atualize a lista.';
      }
      if (causa.codigo === CODIGO_COLETA_DESLIGADA) {
        return 'A coleta de e-mail está desligada neste ambiente, então não há como reprocessar agora.';
      }
      if (causa.status === 404) return 'Você não tem acesso ao reprocesso desta caixa.';
    } else {
      if (causa.codigo === CODIGO_TRIAGEM_EM_ANDAMENTO) {
        return 'Outra triagem da caixa já está em andamento. Aguarde alguns instantes e atualize a lista.';
      }
      if (causa.codigo === CODIGO_COLETA_DESLIGADA) {
        return 'A coleta e a triagem de e-mail estão desligadas neste ambiente, então não há como triar agora.';
      }
      if (causa.codigo === CODIGO_CAIXA_SEM_ROTA) {
        return 'A caixa de automação não tem núcleo configurado, então nenhum e-mail pode ser triado. Peça a configuração do roteamento da caixa.';
      }
      if (causa.status === 404) return 'Você não tem acesso à triagem desta caixa.';
    }
  }
  return acao === 'coleta'
    ? 'Não foi possível reprocessar agora. Tente de novo em alguns instantes.'
    : 'Não foi possível triar agora. Tente de novo em alguns instantes.';
}

function textoDaSituacao(falha: FalhaDeEmail): string {
  if (falha.etapa === 'desconhecida') return 'Etapa que este painel ainda não sabe nomear.';
  if (falha.etapa === 'triagem') {
    // Triagem de outra caixa: o botão não a alcança, e prometer "pode ser triada" seria uma frase que ele nunca cumpre.
    return falha.triavel ? 'Pode ser triada de novo.' : 'Esta caixa não é triada por esta tela.';
  }
  if (falha.esgotada) return 'Esgotou as tentativas: o reprocesso não a tenta mais.';
  // Coleta que ainda não esgotou, mas que o botão não alcança: é de outra caixa.
  if (!falha.reprocessavel) return 'Esta caixa não é reprocessada por esta tela.';
  return 'Pode ser reprocessada.';
}

/**
 * A fila de erro de e-mail (`NR-141`): o que a coleta e a triagem não
 * conseguiram processar. Ninguém precisa abrir log para saber que um e-mail
 * ficou para trás.
 *
 * Quatro estados, na ordem da fila de triagem: carregando, erro, vazio e
 * dados. Erro SEMPRE antes do vazio — API fora do ar não é "nenhuma falha".
 *
 * Não mostra o detalhe da exceção: ele pode citar assunto e remetente.
 */
export function FalhasDeEmail(): React.JSX.Element {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [parametros, definirParametros] = useSearchParams();
  const pagina = lerPagina(parametros.get('pagina'));

  const consulta = useQuery(opcoesDasFalhasDeEmail(pagina));

  // O resumo diz se há falha de triagem: é ele que decide se o botão da triagem aparece (a lista é paginada).
  const resumo = useQuery(opcoesDoResumoDasFalhasDeEmail());

  const [confirmando, definirConfirmando] = useState<Acao | null>(null);
  const [enviando, definirEnviando] = useState(false);
  const [desfecho, definirDesfecho] = useState<Desfecho | null>(null);
  const [erroDaAcao, definirErroDaAcao] = useState<string | null>(null);
  const alertaDoResultado = useRef<HTMLDivElement>(null);
  const resultadoPedeFoco = useRef(false);

  // Depois do POST o diálogo fecha e o foco iria de volta ao botão: leva-o ao resultado, que é o que a
  // pessoa veio ver. Só quando o diálogo já fechou, para vencer a devolução do foco do próprio diálogo, e só
  // por causa de um resultado NOVO: cancelar um diálogo com um resultado antigo na tela devolve o foco ao botão.
  useEffect(() => {
    if (desfecho !== null && confirmando === null && resultadoPedeFoco.current) {
      resultadoPedeFoco.current = false;
      alertaDoResultado.current?.focus();
    }
  }, [desfecho, confirmando]);

  // Sessão expirada no meio da leitura: mesma saída que a escrita dá.
  useEffect(() => {
    if (consulta.error instanceof ErroDaApi && consulta.error.status === 401) {
      void navegar('/entrar', { replace: true });
    }
  }, [consulta.error, navegar]);

  const definirPagina = useCallback(
    (valor: number) => {
      definirParametros(
        (atual) => {
          const proximos = new URLSearchParams(atual);
          if (valor <= 1) proximos.delete('pagina');
          else proximos.set('pagina', String(valor));
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros],
  );

  const executar = (acao: Acao): void => {
    definirEnviando(true);
    definirErroDaAcao(null);
    definirDesfecho(null);
    const chamada: Promise<Desfecho> =
      acao === 'coleta'
        ? reprocessarFalhasDeEmail().then((resultado) => ({ acao, resultado }))
        : triarEmailsDaCaixa().then((resultado) => ({ acao, resultado }));
    chamada
      .then((feito) => {
        resultadoPedeFoco.current = true;
        definirDesfecho(feito);
        // A mesma raiz da lista e do resumo: a faixa da fila de triagem também se atualiza.
        void cliente.invalidateQueries({ queryKey: chavesDasFalhasDeEmail.raiz });
      })
      .catch((causa: unknown) => {
        const mensagem = mensagemDeErro(acao, causa);
        if (mensagem === null) {
          void navegar('/entrar', { replace: true });
          return;
        }
        definirErroDaAcao(mensagem);
        // 409 de trava: a fila pode ter andado por outro caminho.
        void cliente.invalidateQueries({ queryKey: chavesDasFalhasDeEmail.raiz });
      })
      .finally(() => {
        definirConfirmando(null);
        definirEnviando(false);
      });
  };

  const dados = consulta.data;
  const itens = dados?.itens ?? [];
  const temProximaPagina = dados !== undefined && pagina * TAMANHO_DE_PAGINA < dados.total;
  // Só aparece quando há o que reprocessar: botão que sempre falha ou sempre
  // "não encontra nada" ensina a ignorá-lo. A lista é paginada e o reprocesso
  // consome da falha mais ANTIGA, que a tela mostra por último: quando há mais de
  // uma página, a reprocessável pode estar em outra, então o botão fica à mão e a
  // rodada diz "nenhuma estava pronta" se não houver.
  const haMaisDeUmaPagina = dados !== undefined && dados.total > TAMANHO_DE_PAGINA;
  const haReprocessavel = itens.some((falha) => falha.reprocessavel) || haMaisDeUmaPagina;
  // O botão da triagem vem do resumo, que conta tudo (não só a página aberta) e só as falhas da caixa que a tela
  // tria. Enquanto o resumo não responde, ou se ele falhar, cai para o que a página aberta mostra: o botão não some
  // por causa de um aviso que não carregou.
  const haTriavel =
    resumo.data !== undefined ? resumo.data.triaveis > 0 : itens.some((falha) => falha.triavel);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Falhas de e-mail</Typography>
        <Typography variant="body2" color="text.secondary">
          E-mails que a coleta ou a triagem não conseguiram processar e que ainda estão abertos. Nenhum e-mail some em
          silêncio: ele fica aqui até ser resolvido.
        </Typography>
      </Box>

      {/* A região fica SEMPRE montada: leitor de tela só anuncia mudança dentro de uma
          região viva que já existia, não uma que nasce já com o texto. */}
      <Box role="status" aria-live="polite">
        {desfecho !== null ? (
          <Alert
            ref={alertaDoResultado}
            tabIndex={-1}
            severity={severidadeDoDesfecho(desfecho)}
            variant="outlined"
            role="none"
          >
            {textoDoDesfecho(desfecho)}
          </Alert>
        ) : null}
      </Box>

      {erroDaAcao !== null ? <Alert severity="error">{erroDaAcao}</Alert> : null}

      {/* Dois sinais SILENCIOSOS do parser/agente de IA: a leitura que "deu certo" sem agente, e o e-mail
          cuja leitura automática desistiu. Nenhum dos dois vira linha de falha, então a tabela abaixo
          nunca os mostra sozinha. */}
      {resumo.data !== undefined && textoDeLeituraSemAgente(resumo.data) !== null ? (
        <Alert severity="warning" variant="outlined">
          {textoDeLeituraSemAgente(resumo.data)}
        </Alert>
      ) : null}

      {resumo.data !== undefined && textoDeLeituraDesistida(resumo.data) !== null ? (
        <Alert severity="warning" variant="outlined">
          {textoDeLeituraDesistida(resumo.data)}
        </Alert>
      ) : null}

      {/* Duas ações, cada uma com a linha que diz o que alcança: a da coleta busca de novo na caixa do provedor, a da
          triagem só atribui o que já está guardado. Empilham no celular. */}
      {haReprocessavel || haTriavel ? (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
          {haReprocessavel ? (
            <Box sx={{ maxWidth: { md: '34ch' } }}>
              <Button
                variant="contained"
                disabled={enviando}
                onClick={() => {
                  definirConfirmando('coleta');
                }}
              >
                Reprocessar falhas de coleta
              </Button>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
                Busca de novo, na caixa de e-mail, as mensagens que falharam na coleta.
              </Typography>
            </Box>
          ) : null}
          {haTriavel ? (
            <Box sx={{ maxWidth: { md: '34ch' } }}>
              <Button
                variant="outlined"
                disabled={enviando}
                onClick={() => {
                  definirConfirmando('triagem');
                }}
              >
                Triar e-mails da caixa
              </Button>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
                Atribui a tickets os e-mails da caixa que ainda não viraram ticket, os que falharam na triagem entre eles.
              </Typography>
            </Box>
          ) : null}
        </Stack>
      ) : null}

      <Paper variant="outlined">
        {consulta.isPending ? <EstadoDeCarregamento /> : null}

        {consulta.isError ? (
          <EstadoDeErro
            erro={consulta.error}
            contexto="Não foi possível carregar as falhas de e-mail"
            aoReprocessar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}

        {consulta.isSuccess && itens.length === 0 && consulta.data.total === 0 ? (
          <EstadoVazio
            titulo="Nenhuma falha de e-mail aberta"
            descricao="A API respondeu normalmente; nenhum e-mail está parado na coleta ou na triagem."
          />
        ) : null}

        {/* Página vazia COM falhas no total (página fora do intervalo, ou o reprocesso
            esvaziou a página aberta): dizer "nenhuma falha aberta" aqui seria falso. */}
        {consulta.isSuccess && itens.length === 0 && consulta.data.total > 0 ? (
          <EstadoVazio
            titulo="Esta página não tem falhas"
            descricao={`Há ${contagem(consulta.data.total, 'falha aberta', 'falhas abertas')} em outras páginas.`}
            acao={{
              rotulo: 'Ir para a primeira página',
              aoClicar: () => {
                definirPagina(1);
              },
            }}
          />
        ) : null}

        {consulta.isSuccess && itens.length > 0 ? (
          <TableContainer>
            <Table aria-label="Falhas de e-mail">
              <TableHead>
                <TableRow>
                  <TableCell>Etapa</TableCell>
                  <TableCell>Caixa</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Tentativas</TableCell>
                  <TableCell>Primeira ocorrência</TableCell>
                  <TableCell>Última ocorrência</TableCell>
                  <TableCell>Situação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.map((falha) => (
                  <TableRow key={falha.id} hover>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={ROTULOS_DE_ETAPA[falha.etapa]} />
                    </TableCell>
                    {/* Sem quebra no meio da palavra: caixa e motivo são chaves, lidas inteiras. */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Identificador valor={falha.caixa} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Identificador valor={falha.motivo} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <span>{falha.tentativas}</span>
                        {/* Texto, não só cor: o selo precisa dizer o que é. */}
                        {falha.esgotada ? <Chip size="small" color="warning" label="Esgotada" /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatarDataHora(falha.primeiraEm)}</TableCell>
                    <TableCell>{formatarDataHora(falha.ultimaEm)}</TableCell>
                    <TableCell>{textoDaSituacao(falha)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Paper>

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="outlined"
          disabled={pagina <= 1}
          onClick={() => {
            definirPagina(pagina - 1);
          }}
        >
          Página anterior
        </Button>
        <Typography variant="body2" color="text.secondary">
          Página {pagina}
          {dados !== undefined
            ? ` de ${String(Math.max(1, Math.ceil(dados.total / TAMANHO_DE_PAGINA)))} — ${String(dados.total)} no total`
            : ''}
        </Typography>
        <Button
          variant="outlined"
          disabled={!temProximaPagina}
          onClick={() => {
            definirPagina(pagina + 1);
          }}
        >
          Próxima página
        </Button>
      </Stack>

      <ConfirmacaoHumana
        aberto={confirmando === 'coleta'}
        titulo="Reprocessar falhas de coleta?"
        descricao={`Vamos tentar buscar de novo, na caixa de e-mail configurada, até ${String(LOTE_DO_REPROCESSO)} mensagens que falharam na coleta. Nada é enviado para fora do sistema.`}
        rotuloDeConfirmar="Reprocessar"
        aoConfirmar={() => {
          executar('coleta');
        }}
        aoCancelar={() => {
          definirConfirmando(null);
        }}
        ocupado={enviando}
      />

      <ConfirmacaoHumana
        aberto={confirmando === 'triagem'}
        titulo="Triar e-mails da caixa?"
        descricao={`Vamos atribuir a tickets até ${String(LOTE_DA_TRIAGEM)} e-mails da caixa que ainda não viraram ticket. Os que falharam antes estão entre eles. Nada é enviado para fora do sistema.`}
        rotuloDeConfirmar="Triar"
        aoConfirmar={() => {
          executar('triagem');
        }}
        aoCancelar={() => {
          definirConfirmando(null);
        }}
        ocupado={enviando}
      />
    </Stack>
  );
}
